import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { PaymentAssignmentStatusId } from '../PaymentAssignment/Utils/types'
import {
  STATUS_FIELD_CONFIG,
  StatusUpdateFormKey,
  getStatusFieldErrors,
} from '../PaymentAssignment/CaseManagement/statusUpdate.type'
import { ALL_FIELD_KEYS, fieldCsvHeader } from './bulkStatus.constants'
import type { ParsedRow } from './bulkStatus.types'

// Enable custom parse format plugin for strict date parsing
dayjs.extend(customParseFormat)

// ---------------------------------------------------------------------------
// Date parsing helpers for status-specific field columns
// ---------------------------------------------------------------------------

/**
 * Status-specific date field keys that should be normalized during CSV parsing.
 * These fields accept MM-DD-YYYY or MM/DD/YYYY format and normalize to MM-DD-YYYY.
 *
 * Status-specific field columns are additional fields required by certain statuses:
 * - ACTUAL_PAYMENT_DATE: Required for AP_CONFIRMED, PAYMENT_CLEARED statuses
 * - UPDATE_BENEFIT_END_DATE: Required for TERMED status
 * - UPDATE_EFFECTIVE_DATE: Required for TERMED status
 */
const DATE_FIELD_KEYS: Set<StatusUpdateFormKey> = new Set([
  StatusUpdateFormKey.ACTUAL_PAYMENT_DATE,
  StatusUpdateFormKey.UPDATE_BENEFIT_END_DATE,
  StatusUpdateFormKey.UPDATE_EFFECTIVE_DATE,
])

/**
 * Parses a date string from CSV that may be in MM-DD-YYYY or MM/DD/YYYY format
 * and normalizes it to MM-DD-YYYY format using dayjs for validation.
 *
 * Validates that the date is a real valid date (e.g., 02/30/2026 is invalid).
 *
 * Supported input formats:
 * - MM-DD-YYYY (e.g., "07-31-2026")
 * - MM/DD/YYYY (e.g., "07/31/2026")
 * - M-D-YYYY (e.g., "7-31-2026") - single digit month/day
 * - M/D/YYYY (e.g., "7/31/2026") - single digit month/day
 *
 * Output format: MM-DD-YYYY (always with leading zeros)
 *
 * @param value - The raw date string from CSV
 * @returns Normalized date string in MM-DD-YYYY format, or original value if invalid
 *
 * @example
 * parseDateField("07-31-2026") // "07-31-2026"
 * parseDateField("07/31/2026") // "07-31-2026"
 * parseDateField("7-31-2026")  // "07-31-2026"
 * parseDateField("7/31/2026")  // "07-31-2026"
 * parseDateField("12/5/2026")  // "12-05-2026"
 * parseDateField("18/25/2026") // "18/25/2026" (invalid - returned as-is for validation error)
 * parseDateField("02/30/2026") // "02/30/2026" (invalid - Feb 30 doesn't exist)
 */
export const parseDateField = (value: string): string => {
  if (!value) return value

  const trimmed = value.trim()

  // Try parsing with MM-DD-YYYY format (strict mode)
  let parsed = dayjs(trimmed, 'MM-DD-YYYY', true)
  if (parsed.isValid()) {
    return parsed.format('MM-DD-YYYY')
  }

  // Try parsing with M-D-YYYY format (allows single digit month/day)
  parsed = dayjs(trimmed, 'M-D-YYYY', true)
  if (parsed.isValid()) {
    return parsed.format('MM-DD-YYYY')
  }

  // Try parsing with MM/DD/YYYY format
  parsed = dayjs(trimmed, 'MM/DD/YYYY', true)
  if (parsed.isValid()) {
    return parsed.format('MM-DD-YYYY')
  }

  // Try parsing with M/D/YYYY format (allows single digit month/day)
  parsed = dayjs(trimmed, 'M/D/YYYY', true)
  if (parsed.isValid()) {
    return parsed.format('MM-DD-YYYY')
  }

  // Return empty string if no format matched (will be caught by validation)
  return ''
}

/**
 * Checks if a status-specific field key is a date field that should be normalized during CSV parsing.
 *
 * Status-specific date fields include:
 * - ACTUAL_PAYMENT_DATE (Actual Payment Date) - for AP_CONFIRMED, PAYMENT_CLEARED
 * - UPDATE_BENEFIT_END_DATE (Benefit End Date) - for TERMED
 * - UPDATE_EFFECTIVE_DATE (New Effective Date) - for TERMED
 *
 * @param fieldKey - The StatusUpdateFormKey to check
 * @returns true if the field is a date field that accepts MM-DD-YYYY or MM/DD/YYYY format
 */
export const isDateField = (fieldKey: StatusUpdateFormKey): boolean =>
  DATE_FIELD_KEYS.has(fieldKey)

// ---------------------------------------------------------------------------
// Row validation & shared field derivations (used by both the upload and
// preview steps).
// ---------------------------------------------------------------------------

export const rowFieldValues = (row: ParsedRow): Record<string, unknown> => ({
  ...row.fields,
  [StatusUpdateFormKey.NOTE]: row.note,
})

/**
 * For fields backed by a fixed option list (e.g. the Yes/No selects),
 * canonicalizes a raw CSV value to the option's exact casing so it both
 * displays correctly in the Select and matches on the nose downstream.
 * Accepts any casing ("YES", "yes", "YEs") and maps it to "Yes".
 * Returns the trimmed original value unchanged when there's no case-
 * insensitive match (e.g. free-text fields, or truly invalid values —
 * those are still caught by getStatusFieldErrors).
 */
const normalizeOptionValue = (
  value: string,
  options: readonly string[] | undefined
): string => {
  if (!options || options.length === 0) return value
  const trimmed = value.trim()
  const match = options.find((o) => o.toLowerCase() === trimmed.toLowerCase())
  return match ?? trimmed
}

/**
 * Normalizes every option-backed field on a row (status-dependent) so CSV
 * casing variations (e.g. "yes" / "YES" / "YEs") are coerced to the
 * canonical option casing (e.g. "Yes") before the row is stored/rendered.
 */
export const normalizeRowFieldOptions = (row: ParsedRow): ParsedRow => {
  if (!row.status) return row
  const rules = STATUS_FIELD_CONFIG[row.status] ?? []
  if (rules.length === 0) return row

  let changed = false
  const nextFields = { ...row.fields }
  for (const rule of rules) {
    const raw = nextFields[rule.key]
    if (!raw) continue
    const normalized = normalizeOptionValue(raw, rule.options)
    if (normalized !== raw) {
      nextFields[rule.key] = normalized
      changed = true
    }
  }
  return changed ? { ...row, fields: nextFields } : row
}

const EMPTY_DUPLICATE_SET: ReadonlySet<string> = new Set()

/**
 * Returns the set of trimmed, case-insensitive benefitIds that appear on more
 * than one row. Rows whose benefitId is in this set are treated as invalid so
 * a bulk submit never sends the same benefitId twice in one batch (which
 * would otherwise cause ambiguous error attribution / partial removal).
 */
export const computeDuplicateBenefitIds = (rows: ParsedRow[]): Set<string> => {
  const seen = new Set<string>()
  const duplicates = new Set<string>()
  for (const row of rows) {
    const id = row.benefitId.trim().toLowerCase()
    if (!id) continue
    if (seen.has(id)) {
      duplicates.add(id)
    } else {
      seen.add(id)
    }
  }
  return duplicates
}

export const getRowErrors = (
  row: ParsedRow,
  duplicateBenefitIds: ReadonlySet<string> = EMPTY_DUPLICATE_SET,
  allowedStatuses?: ReadonlyArray<PaymentAssignmentStatusId>
): string[] => {
  const errors: string[] = []
  const trimmedBenefitId = row.benefitId.trim()
  if (!trimmedBenefitId) {
    errors.push('Benefit ID is required')
  } else if (duplicateBenefitIds.has(trimmedBenefitId.toLowerCase())) {
    errors.push('Duplicate Benefit ID (same ID used on another row)')
  }
  if (!row.status) {
    errors.push('Status is required')
  } else {
    if (
      allowedStatuses &&
      !(allowedStatuses as string[]).includes(row.status)
    ) {
      errors.push(`Status "${row.status}" is not permitted`)
    } else {
      errors.push(...getStatusFieldErrors(row.status, rowFieldValues(row)))
    }
  }
  return errors
}

export const isValidRow = (
  row: ParsedRow,
  duplicateBenefitIds: ReadonlySet<string> = EMPTY_DUPLICATE_SET,
  allowedStatuses?: ReadonlyArray<PaymentAssignmentStatusId>
): boolean =>
  getRowErrors(row, duplicateBenefitIds, allowedStatuses).length === 0

/**
 * Builds a CSV record for a row using the same column shape as the
 * downloadable template (benefitId, status, note, then every status-specific
 * field column keyed by its human-readable header).
 */
export const rowToCsvRecord = (row: ParsedRow): Record<string, string> => {
  const record: Record<string, string> = {
    benefitId: row.benefitId,
    status: row.status,
    note: row.note,
  }
  for (const key of ALL_FIELD_KEYS) {
    record[fieldCsvHeader(key)] = row.fields[key] ?? ''
  }
  return record
}

/** Whether a specific field column is active for a row (status matches + when() passes). */
export const isFieldActiveForRow = (
  row: ParsedRow,
  fieldKey: StatusUpdateFormKey
): boolean => {
  if (!row.status) return false
  const rules = STATUS_FIELD_CONFIG[row.status] ?? []
  const rule = rules.find((r) => r.key === fieldKey)
  if (!rule) return false
  return rule.when ? rule.when(rowFieldValues(row)) : true
}

/** Whether a field column is required for a row. */
export const isFieldRequiredForRow = (
  row: ParsedRow,
  fieldKey: StatusUpdateFormKey
): boolean => {
  if (!row.status) return false
  const rules = STATUS_FIELD_CONFIG[row.status] ?? []
  const rule = rules.find((r) => r.key === fieldKey)
  return rule?.required ?? false
}

// ---------------------------------------------------------------------------
// CSV template generation
// ---------------------------------------------------------------------------

/** Realistic example values keyed by StatusUpdateFormKey, used in the downloadable template. */
const EXAMPLE_VALUES: Partial<Record<StatusUpdateFormKey, string>> = {
  [StatusUpdateFormKey.ACTUAL_PREMIUM_PAID]: '450.00',
  [StatusUpdateFormKey.ACTUAL_PAYMENT_DATE]: '07-31-2026',
  [StatusUpdateFormKey.CONFIRMATION_CODE]: 'CNF-123456',
  [StatusUpdateFormKey.DID_SET_UP_AUTOPAY]: 'Yes',
  [StatusUpdateFormKey.AUTOPAY_PAYMENT_METHOD]: 'VCN',
  [StatusUpdateFormKey.AUTOPAY_PULL_DAY]: '15',
  [StatusUpdateFormKey.TICKET]: 'TICKET-001',
  [StatusUpdateFormKey.ESCALATE_REASON]: 'Payment Issue',
  [StatusUpdateFormKey.ESCALATE_EXPLANATION]:
    'Member called in to dispute charge',
  [StatusUpdateFormKey.EE_CONTACT]: 'Yes (optional)',
  [StatusUpdateFormKey.HR_CONTACT]: 'No (optional)',
  [StatusUpdateFormKey.DID_MAKE_PAYMENT]: 'No',
  [StatusUpdateFormKey.UPDATE_BENEFIT_END_DATE]: '12-31-2026',
  [StatusUpdateFormKey.UPDATE_EFFECTIVE_DATE]: '08-01-2026',
  [StatusUpdateFormKey.PAYMENT_BOT_VOICE_RECORDING_URL]:
    'https://recordings.example.com/abc123',
}

/**
 * Builds one example CSV row per allowed status, using rule-aware example
 * values so option constraints (e.g. discrete Select values) are respected.
 */
export function buildCsvTemplateRows(
  allowedStatuses: PaymentAssignmentStatusId[]
): Record<string, string>[] {
  return allowedStatuses.map((status, i) => {
    const rules = STATUS_FIELD_CONFIG[status] ?? []
    const row: Record<string, string> = {
      benefitId: `xxxxxxxx-xxxx-xxxx-xxxx-xxxx${String(i + 1).padStart(8, '0')}`,
      status,
      note: `Example note for ${status}`,
    }
    // Fill field columns: use rule-aware example values so option constraints are respected
    for (const key of ALL_FIELD_KEYS) {
      const rule = rules.find((r) => r.key === key)
      if (!rule) {
        row[fieldCsvHeader(key)] = ''
        continue
      }
      // If the rule restricts to specific options, use the first valid option
      // as the example (overrides the generic EXAMPLE_VALUES for this row)
      if (rule.options && rule.options.length > 0) {
        const generic = EXAMPLE_VALUES[key]
        row[fieldCsvHeader(key)] =
          generic && rule.options.includes(generic)
            ? generic
            : String(rule.options[0])
      } else {
        row[fieldCsvHeader(key)] = EXAMPLE_VALUES[key] ?? ''
      }
    }
    return row
  })
}
